<!--
Copyright (c) 2020 ForgeRock. All rights reserved.

This software may be modified and distributed under the terms
of the MIT license. See the LICENSE file for details.
-->

<template>
    <div class="main-div">
        <pearson-nav
            :themeName="themeName"
            :showBrandBadge="ui.showBrandBadge"
            :showNavRegion="ui.showNavRegion" />
        <div :class="[`${themeName.toLowerCase()}-background-image`, 'page-container']"
            :data-type="selfServiceType">
            <pearson-logo
                :logoType="ui.logoType"
                :logoSrc="ui.logo || pearsonLogoUrl()"
                :logoAlt="ui.logoAlt"
                :logoWidth="ui.logoWidth"
                :logoHeight="ui.logoHeight"
                :logoClass="ui.logoClass"
                :logoId="ui.logoId"
                :logoContainerClass="ui.logoContainerClass"
                :logoImages="ui.logoImages"
                :welcomeText="$t('pages.selfservice.forgotUsername.welcomeText')" />
            <fr-center-card :class="[{ 'faethm-right-align-div': themeName === 'FAETHM' },'pearson-card']" :showLogo="false" v-if="selfServiceType !== null">
                <div slot="center-card-header">
                    <div v-if="themeName === 'FAETHM'">
                        <img class="main-logo side-logo faethm-pearson-logo" :src="ui.logo" width="140px" />
                    </div>
                    <a class="back" :class="{ 'faethm-back-link': themeName === 'FAETHM' }" @click="backUrl" :href="gotoLink">{{$t('pages.selfservice.forgotUsername.returnToSignInAnchorText')}}</a>
                    <h1 class="h2">{{$t(`pages.selfservice.headers.username.title`)}}</h1>
                </div>

                <b-card-body slot="center-card-body">
                    <component ref="selfServiceStage"
                        :is="selfServiceType"
                        :selfServiceDetails="selfServiceDetails"
                        @advanceStage="advanceStage"
                        :apiType="apiType">
                    </component>
                </b-card-body>
            </fr-center-card>

            <b-container fluid class="h-100 px-0" v-else>
                <div class="h-100 d-flex">
                    <div class="m-auto fr-center-card">
                        <bounce-loader :color="loadingColor"></bounce-loader>
                    </div>
                </div>
            </b-container>
        </div>

        <pearson-footer
            :themeName="themeName"
            :showFooter="ui.showFooter"
            :links="links"
            :ui="ui" />
    </div>
</template>

<script>
import _ from 'lodash';
import { BounceLoader } from 'vue-spinner/dist/vue-spinner.min.js';
import styles from '@/scss/theme-variables.scss';
import CenterCard from '@/components/utils/CenterCard';
import Captcha from '@/components/selfservice/common/Captcha';
import EmailUsername from '@/components/selfservice/forgotusername/EmailUsername';
import GenericSelfService from '@/components/selfservice/common/GenericSelfService';
import RetrieveUsername from '@/components/selfservice/forgotusername/RetrieveUsername';
import SelfserviceAPI from '@/components/selfservice/mixins/SelfserviceAPIMixin';
import UserQuery from '@/components/selfservice/forgotusername/ForgotUsername';
import ThemeMixin from '../../../components/common/mixins/ThemeMixin';
import PearsonNav from '../../../components/common/layout/PearsonNav.vue';
import PearsonLogo from '../../../components/common/layout/PearsonLogo.vue';
import PearsonFooter from '../../../components/common/layout/PearsonFooter.vue';

/**
 * @description Selfservice controlling component for retrieving a forgotten username. Makes use of selfservice-username.json config file.
 *
 * @mixin - selfservice/mixins/SelfserviceAPIMixin.vue
 */
export default {
    name: 'Forgot-Username',
    components: {
        Captcha,
        EmailUsername,
        RetrieveUsername,
        UserQuery,
        GenericSelfService,
        'bounce-loader': BounceLoader,
        'fr-center-card': CenterCard,
        PearsonNav,
        PearsonLogo,
        PearsonFooter
    },
    computed: {
        gotoLink () {
            return this.sanitizeUrl(this.$route.query.goto) || null;
        }
    },
    data () {
        return {
            selfServiceType: null,
            selfServiceDetails: null,
            loadingColor: styles.baseColor,
            apiType: 'username'
        };
    },
    mounted () {
        /* istanbul ignore next */
        this.loadData();
        this.updateDocumentTitle();
    },
    watch: {
        '$i18n.locale' () {
            this.updateDocumentTitle();
        }
    },
    methods: {
        updateDocumentTitle () {
            const translatedTitle = this.$t('pages.selfservice.headers.username.title');
            const brandSuffix = 'Pearson';
            if (translatedTitle) {
                document.title = `${translatedTitle} | ${brandSuffix}`;
            }
        },
        sanitizeUrl (url) {
            if (!url || typeof url !== 'string') {
                console.log('Back to sign-in URL is null/undefined or not a string', url);
                return null;
            }
            // First check for the specific malicious payload and other dangerous patterns
            const backtoSigninUrl = url.toLowerCase();
            const sanitizedUrl = [
                'javascript:',
                'data:',
                'vbscript:',
                'eval(',
                'atob(',
                'fetch(',
                'xmlhttprequest',
                // Check for the specific base64 encoded payload
                'zmv0y2goimg0dhbzoi8vaWftlxn0ywdllnblyxjzb24uy29tl2f1dgganzvbi9yzwfsbxmvcm9vdc9yzwfsbxmvcgvhcnivbi91c2vycu9fyWn0aw9upwlkrnhvbvnlc3npb24i'
            ];
            for (let urlParams of sanitizedUrl) {
                if (backtoSigninUrl.includes(urlParams)) {
                    return null;
                }
            }
            // Remove any potential script injections
            const cleanUrl = url.replace(/javascript:/gi, '')
                .replace(/data:/gi, '')
                .replace(/vbscript:/gi, '')
                .replace(/on\w+=/gi, '');
            // Allow only HTTP, HTTPS, and relative URLs
            const urlPattern = /^(https?:\/\/|\/|\.\/|\?)/i;
            // Check for valid URL format
            if (!urlPattern.test(cleanUrl.trim())) {
                return null;
            }
            // Additional regex check for XSS patterns
            const xssURLPatterns = [
                /<script/gi,
                /on\w+\s*=/gi,
                /expression\(/gi,
                /eval\(/gi,
                /atob\(/gi,
                /fetch\(/gi,
                // Regex for the specific base64 payload
                /ZmV0Y2goImh0dHBzOi8vaWFtLXN0YWdlLnBlYXJzb24uY29tL2F1dGgvanNvbi9yZWFsbXMvcm9vdC9yZWFsbXMvcGVhcnNvbi91c2Vycz9fYWN0aW9uPWlkRnJvbVNlc3Npb24i/gi
            ];
            for (let pattern of xssURLPatterns) {
                if (pattern.test(cleanUrl)) {
                    return null;
                }
            }
            return cleanUrl.trim();
        },
        backUrl (event) {
            const goToUrl = this.sanitizeUrl(this.$route.query.goto);
            if (!goToUrl) {
                event.preventDefault();
                window.location.assign(window.location.origin + '/auth/XUI/?realm=/pearson&service=AuthN#login/');
            } else {
                // Let the href attribute handle the redirect
                console.log('Redirecting to:', goToUrl);
            }
        },
        setChildComponent (type, details) {
            this.selfServiceDetails = details;

            let stageCheck = false;

            _.each(this.$options.components, (value, key) => {
                if (_.toLower(key) === _.toLower(type)) {
                    stageCheck = true;
                }
            });

            if (stageCheck) {
                this.selfServiceType = type;
            } else {
                this.selfServiceType = 'GenericSelfService';
            }
        },
        apiErrorCallback (error) {
            /* istanbul ignore next */
            if (typeof window.gaEvent === 'function') {
                window.gaEvent('Forgot username', 'Forgot username', 'Failed', error.response.data.message);
            }
            this.setChildComponent('retrieveUsername', { error: error.response.data.message });
        }
    },
    mixins: [
        SelfserviceAPI,
        ThemeMixin
    ]
};
</script>
<style scoped lang="scss">
.forgot-username-container {
    display: flex;
    flex-direction: row;
}
.h2 {
    font-size: 2rem;
    line-height: 2.71429rem;
    font-weight: lighter;
    color: #252525;
    font-family: "Open Sans Complete"
}
@import '../../../scss/pearson-common.css';
</style>
